import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import { axisProps, fmtMoney, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Brands of one generic, in the same form and strength, side by side at what each cost on its
// latest delivery (or its opening stock's cost, or the catalogue's, for one not delivered yet): the dear brand of omeprazole 20 mg next to the cheap one. Liquids, creams and
// gels are compared per ml or per g, because one brand's bottle is 15 ml and another's 20.
//
// Matching needs a strength, read from the Strength field or the name ("Panadol 500mg"), and for a
// liquid the bottle size. An item missing either cannot be matched with anything; the second table
// lists those whose generic has another brand, so the catalogue can be put right.

const fetcher = () => reportsAPI.getGenericPrices();

const pct = (v) => (v === null || v === undefined ? '' : `+${Number(v)}%`);

const gapTone = (v) => {
  const n = Number(v || 0);
  if (n >= 50) return 'text-rose-600';
  if (n >= 25) return 'text-amber-700';
  return 'text-gray-700';
};

const GAPS = [
  { key: 0, label: 'All' },
  { key: 25, label: 'Gap of 25% or more' },
  { key: 50, label: '50% or more' },
  { key: 100, label: 'Double or more' },
];

// A brand not delivered since the system started is priced at its opening stock's cost (the old
// system's), else the catalogue's, and the list says which.
const SOURCE_TAG = {
  OPENING: { label: 'opening stock', title: 'Not delivered since the system started: the cost it was counted in at' },
  CATALOGUE: { label: 'catalogue', title: "Never delivered: the catalogue's price" },
};

const brandText = (b) =>
  `${b.name} ${fmtMoney(b.costPerBasis)}${SOURCE_TAG[b.costSource] ? ` (${SOURCE_TAG[b.costSource].label})` : ''}${b.discontinued ? ' (not reordered)' : ''}`;

const COLUMNS = [
  {
    key: 'label', label: 'Generic', wrap: true,
    render: (r) => (
      <div>
        <p className="font-semibold text-gray-900">{r.generic}</p>
        <p className="text-xs text-gray-500">{r.form} · {r.strength}</p>
      </div>
    ),
    csv: (r) => r.label,
    searchText: (r) => `${r.label} ${(r.brands || []).map((b) => b.name).join(' ')}`,
  },
  {
    key: 'brands', label: 'Brands, cheapest first', wrap: true,
    render: (r) => {
      const list = r.brands || [];
      return (
        <ul className="space-y-0.5">
          {list.map((b, i) => (
            <li key={b.id} className="text-xs flex items-baseline gap-1.5 flex-wrap">
              <span className={`font-medium ${i === 0 ? 'text-emerald-700' : i === list.length - 1 ? 'text-rose-700' : 'text-gray-800'}`}>{b.name}</span>
              <span className="tabular-nums text-gray-600">{fmtMoney(b.costPerBasis)}</span>
              {SOURCE_TAG[b.costSource] && (
                <span className="text-[10px] text-gray-400" title={SOURCE_TAG[b.costSource].title}>{SOURCE_TAG[b.costSource].label}</span>
              )}
              {b.discontinued && (
                <span className="text-[10px] text-gray-400" title="Marked discontinued, still in stock">not reordered</span>
              )}
            </li>
          ))}
        </ul>
      );
    },
    csv: (r) => (r.brands || []).map(brandText).join('; '),
    sortValue: (r) => Number(r.brandCount),
  },
  {
    key: 'cheapestCost', label: 'Cheapest', align: 'right',
    render: (r) => (
      <div>
        <p className="font-semibold text-emerald-700">{fmtMoney(r.cheapestCost)}</p>
        <p className="text-xs text-gray-500">{r.basisLabel}</p>
      </div>
    ),
    csv: (r) => r.cheapestCost,
    sortValue: (r) => Number(r.cheapestCost),
  },
  {
    key: 'dearestCost', label: 'Dearest', align: 'right',
    render: (r) => <span className="font-semibold text-rose-700">{fmtMoney(r.dearestCost)}</span>,
    csv: (r) => r.dearestCost,
    sortValue: (r) => Number(r.dearestCost),
  },
  {
    key: 'gapPercent', label: 'Gap', align: 'right',
    render: (r) => <span className={`font-bold ${gapTone(r.gapPercent)}`}>{Number(r.gapPercent) === 0 ? 'Same' : pct(r.gapPercent)}</span>,
    csv: (r) => r.gapPercent,
    sortValue: (r) => Number(r.gapPercent),
  },
  {
    key: 'sellingHigh', label: 'Selling', align: 'right',
    render: (r) => {
      if (r.sellingLow === null || r.sellingLow === undefined) return <span className="text-gray-300">—</span>;
      return Number(r.sellingLow) === Number(r.sellingHigh)
        ? fmtMoney(r.sellingLow)
        : `${fmtMoney(r.sellingLow)} – ${fmtMoney(r.sellingHigh)}`;
    },
    csv: (r) => (r.sellingLow === null || r.sellingLow === undefined ? '' : `${r.sellingLow} - ${r.sellingHigh}`),
    sortValue: (r) => Number(r.sellingHigh ?? 0),
  },
  {
    key: 'paidAboveCheapest', label: 'Paid above cheapest', align: 'right',
    render: (r) => (Number(r.paidAboveCheapest) > 0
      ? <span className="font-semibold text-gray-900">{fmtMoney(r.paidAboveCheapest)}</span>
      : <span className="text-gray-400">{fmtMoney(0)}</span>),
    csv: (r) => r.paidAboveCheapest,
    sortValue: (r) => Number(r.paidAboveCheapest),
  },
];

const LEFT_OUT_COLUMNS = [
  { key: 'name', label: 'Item', wrap: true },
  { key: 'genericName', label: 'Generic', wrap: true },
  { key: 'form', label: 'Form' },
  {
    key: 'unitCost', label: 'Cost per unit', align: 'right',
    render: (r) => (r.unitCost === null || r.unitCost === undefined ? <span className="text-gray-300">—</span> : fmtMoney(r.unitCost)),
    csv: (r) => r.unitCost ?? '',
    sortValue: (r) => Number(r.unitCost ?? 0),
  },
  { key: 'reason', label: 'Why it is not compared', wrap: true },
];

const GenericPricesReport = () => {
  const drill = useDrill();
  const [gap, setGap] = useState(0);
  const { data, loading } = useReport(fetcher);
  const k = data?.kpis || {};
  const days = data?.recentDays ?? 90;

  const rows = useMemo(
    () => (data?.rows || []).filter((r) => Number(r.gapPercent || 0) >= gap),
    [data, gap]
  );

  const chartRows = useMemo(
    () => rows
      .filter((r) => Number(r.gapPercent) > 0)
      .slice(0, 10)
      .map((r) => ({ name: `${r.generic} ${r.strength}`, gap: Number(r.gapPercent) })),
    [rows]
  );

  const kpis = [
    { label: 'Generics compared', value: k.groups ?? 0, sub: `${k.brands ?? 0} brands` },
    { label: 'Wide gaps', value: k.gap25 ?? 0, sub: '25% or more', tone: Number(k.gap25) > 0 ? 'alert' : undefined },
    { label: 'Widest gap', value: k.widest ? pct(k.widest.percent) : '—', sub: k.widest?.label },
    { label: 'Above cheapest', value: fmtMoney(k.paidAboveCheapest ?? 0), sub: `Last ${days} days` },
    { label: 'Not compared', value: k.leftOut ?? 0, sub: 'Strength or size missing' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-gray-500 flex-1 min-w-[240px]">
          As of {data?.asOf || 'today'} · each brand at its latest delivery's cost.
        </p>
        <PrintButton />
      </div>

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Price gap">
        {GAPS.map((g) => (
          <button
            key={g.key}
            type="button"
            aria-pressed={gap === g.key}
            onClick={() => setGap(g.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${gap === g.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {g.label}
          </button>
        ))}
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        Brands of the same generic, form and strength, cheapest first. Liquids, creams and gels are
        compared per ml or per g, since bottle sizes differ. Paid above cheapest is what the last {days} days'
        deliveries of the dearer brands cost beyond the same quantity of the cheapest one at today's
        costs: a guide to the size of the gap, not a saving, since a doctor may want a particular brand.
        Click a row for the brands' suppliers, stock and the generic's other strengths.
      </p>

      {chartRows.length > 0 && (
        <ChartPanel title="Widest gaps between brands">
          <ResponsiveContainer width="100%" height={Math.max(200, chartRows.length * 34)}>
            <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey="name" {...axisProps} width={190} />
              <Tooltip {...tooltipStyle} formatter={(v) => [`${v}%`, 'Dearest over cheapest']} />
              <Bar dataKey="gap" name="Gap" radius={[0, 4, 4, 0]} barSize={16}>
                {chartRows.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'genericGroup', key: r.key })}
        title="Brands compared"
        columns={COLUMNS}
        rows={rows}
        csvName={`generic-prices-${data?.asOf || ''}`}
        emptyText={loading ? 'Loading' : (data?.rows || []).length === 0
          ? 'No generic has two brands of the same form and strength with a price yet'
          : 'No generic has a gap this wide'}
        noun="generic"
        footnote="Opening stock: not delivered since the system started, so priced at the cost it was counted in at (the old system's). Catalogue: never delivered, so the catalogue's price. Not reordered: marked discontinued but still in stock."
      />

      <ReportTable
        title="Brands not compared"
        columns={LEFT_OUT_COLUMNS}
        rows={data?.leftOut || []}
        csvName={`generic-prices-not-compared-${data?.asOf || ''}`}
        emptyText={loading ? 'Loading' : 'Every brand with another of its generic could be compared'}
        noun="item"
        footnote="Brands whose generic has another brand but that could not be matched with it. Record the strength on the medicine (and for a liquid, cream or gel, the bottle or tube size) and it will be compared."
      />

      <ReportPrintLayout
        title="Generic Prices"
        subtitle={`As of ${data?.asOf || ''}`}
        kpis={kpis.map((x) => ({ label: x.label, value: x.value }))}
        tables={[
          { title: 'Brands compared', columns: COLUMNS, rows },
          { title: 'Brands not compared', columns: LEFT_OUT_COLUMNS, rows: data?.leftOut || [] },
        ]}
      />

      {drill.node}
    </div>
  );
};

export default GenericPricesReport;
